import axiosConfig from '@/lib/axiosConfig';
import { API_URL } from '@/config.js';

const materialFilterModule = {
  namespaced: true,
  state: {
    // Результаты поиска и фильтрации
    materials: [],
    totalMaterials: 0,
    pagination: {
      page: 1,
      limit: 30,
      pages: 0
    },
    
    // Доступные опции для фильтров
    filterOptions: {
      locations: [],
      tags: [],
      audience_levels: [],
      moderation_statuses: [],
      festivals: []
    },
    
    // Календарная статистика
    calendarStats: [],
    
    // Текущие фильтры
    activeFilters: {
      query: '',
      year: null,
      month: null,
      country: '',
      city: '',
      audience_level: '',
      moderation_status: '',
      festival_id: null,
      tags: []
    },
    
    // Состояния загрузки и ошибок
    isLoading: false,
    isLoadingOptions: false,
    isLoadingCalendar: false,
    error: null,
    
    // Дополнительные данные
    filtersDescription: '',
    appliedFilters: {}
  },
  
  mutations: {
    // Результаты поиска
    setMaterials(state, materials) {
      state.materials = materials;
    },
    
    setTotalMaterials(state, total) {
      state.totalMaterials = total;
    },
    
    setPagination(state, pagination) {
      state.pagination = { ...state.pagination, ...pagination };
    },
    
    // Опции фильтров
    setFilterOptions(state, options) {
      state.filterOptions = { ...state.filterOptions, ...options };
    },
    
    // Календарная статистика
    setCalendarStats(state, stats) {
      state.calendarStats = stats;
    },
    
    // Фильтры
    setActiveFilter(state, { key, value }) {
      state.activeFilters[key] = value;
    },
    
    setActiveFilters(state, filters) {
      state.activeFilters = { ...state.activeFilters, ...filters };
    },
    
    clearFilters(state) {
      state.activeFilters = {
        query: '',
        year: null,
        month: null,
        country: '',
        city: '',
        audience_level: '',
        moderation_status: '',
        festival_id: null,
        tags: []
      };
    },
    
    // Состояния загрузки
    setIsLoading(state, loading) {
      state.isLoading = loading;
    },
    
    setIsLoadingOptions(state, loading) {
      state.isLoadingOptions = loading;
    },
    
    setIsLoadingCalendar(state, loading) {
      state.isLoadingCalendar = loading;
    },
    
    setError(state, error) {
      state.error = error;
    },
    
    // Дополнительные данные
    setFiltersDescription(state, description) {
      state.filtersDescription = description;
    },
    
    setAppliedFilters(state, filters) {
      state.appliedFilters = filters;
    }
  },
  
  actions: {
    // Поиск материалов с фильтрацией
    async searchMaterials({ commit, state, rootState }, { page = 1, limit = 30 } = {}) {
      commit('setIsLoading', true);
      commit('setError', null);
      
      try {
        const params = {
          action: 'search_materials_advanced',
          page,
          limit,
          ...state.activeFilters
        };
        
        // Добавляем chatbot_name из конфигурации
        if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
          params.chatbot_name = rootState.app.configData.chatbot_name;
        }
        
        // Убираем пустые значения
        Object.keys(params).forEach(key => {
          if (params[key] === '' || params[key] === null || 
              (Array.isArray(params[key]) && params[key].length === 0)) {
            delete params[key];
          }
        });
        
        const response = await axiosConfig.get(API_URL, { params });
        
        if (response.data.result === 'success') {
          commit('setMaterials', response.data.data);
          commit('setPagination', response.data.pagination);
          commit('setFiltersDescription', response.data.filters_description);
          commit('setAppliedFilters', response.data.applied_filters);
        } else {
          throw new Error(response.data.message || 'Search failed');
        }
      } catch (error) {
        console.error('Error searching materials:', error);
        commit('setError', error.message);
      } finally {
        commit('setIsLoading', false);
      }
    },
    
    // Загрузка опций фильтров
    async loadFilterOptions({ commit, rootState }) {
      commit('setIsLoadingOptions', true);
      commit('setError', null);
      
      try {
        const params = {
          action: 'get_filter_options'
        };
        
        // Добавляем chatbot_name из конфигурации
        if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
          params.chatbot_name = rootState.app.configData.chatbot_name;
        }
        
        const response = await axiosConfig.get(API_URL, { params });
        
        if (response.data.result === 'success') {
          commit('setFilterOptions', response.data.data);
        } else {
          throw new Error(response.data.message || 'Failed to load filter options');
        }
      } catch (error) {
        console.error('Error loading filter options:', error);
        commit('setError', error.message);
      } finally {
        commit('setIsLoadingOptions', false);
      }
    },
    
    // Загрузка календарной статистики
    async loadCalendarStats({ commit, rootState }, { channel_id = null } = {}) {
      commit('setIsLoadingCalendar', true);
      commit('setError', null);
      
      try {
        const params = {
          action: 'get_calendar_stats'
        };
        
        // Добавляем chatbot_name из конфигурации
        if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
          params.chatbot_name = rootState.app.configData.chatbot_name;
        }
        
        if (channel_id) {
          params.channel_id = channel_id;
        }
        
        const response = await axiosConfig.get(API_URL, { params });
        
        if (response.data.result === 'success') {
          commit('setCalendarStats', response.data.data);
        } else {
          // Календарная статистика может быть пуста - это не ошибка
          commit('setCalendarStats', []);
        }
      } catch (error) {
        console.error('Error loading calendar stats:', error);
        commit('setCalendarStats', []);
      } finally {
        commit('setIsLoadingCalendar', false);
      }
    },

    // Загрузка календарной статистики с учетом фильтров
    async loadCalendarStatsFiltered({ commit, state, rootState }, { channel_id = null } = {}) {
      commit('setIsLoadingCalendar', true);
      commit('setError', null);
      
      try {
        const params = {
          action: 'get_calendar_stats_filtered'
        };
        
        // Добавляем chatbot_name из конфигурации
        if (rootState.app && rootState.app.configData && rootState.app.configData.chatbot_name) {
          params.chatbot_name = rootState.app.configData.chatbot_name;
        }
        
        if (channel_id) {
          params.channel_id = channel_id;
        }
        
        // Добавляем активные фильтры (исключаем год и месяц чтобы не зациклиться)
        const filters = { ...state.activeFilters };
        delete filters.year;
        delete filters.month;
        
        Object.keys(filters).forEach(key => {
          if (filters[key] !== '' && filters[key] !== null && 
              !(Array.isArray(filters[key]) && filters[key].length === 0)) {
            if (Array.isArray(filters[key])) {
              params[key] = filters[key].join(',');
            } else {
              params[key] = filters[key];
            }
          }
        });
        
        const response = await axiosConfig.get(API_URL, { params });
        
        if (response.data.result === 'success') {
          commit('setCalendarStats', response.data.data);
        } else {
          // Календарная статистика может быть пуста - это не ошибка
          commit('setCalendarStats', []);
        }
      } catch (error) {
        console.error('Error loading filtered calendar stats:', error);
        commit('setCalendarStats', []);
      } finally {
        commit('setIsLoadingCalendar', false);
      }
    },
    
    // Применение фильтра
    async applyFilter({ commit, dispatch }, { key, value }) {
      commit('setActiveFilter', { key, value });
      // Сбрасываем пагинацию при смене фильтров
      commit('setPagination', { page: 1 });
      
      // Если изменился НЕ календарный фильтр, перезагружаем календарь
      if (key !== 'year' && key !== 'month') {
        dispatch('loadCalendarStatsFiltered');
      }
      
      // Выполняем поиск с новыми фильтрами
      return dispatch('searchMaterials');
    },
    
    // Применение множественных фильтров
    async applyFilters({ commit, dispatch }, filters) {
      commit('setActiveFilters', filters);
      commit('setPagination', { page: 1 });
      
      // Перезагружаем календарь если есть не-календарные фильтры
      const hasNonCalendarFilters = Object.keys(filters).some(key => 
        key !== 'year' && key !== 'month' && filters[key] !== '' && filters[key] !== null &&
        !(Array.isArray(filters[key]) && filters[key].length === 0)
      );
      
      if (hasNonCalendarFilters) {
        dispatch('loadCalendarStatsFiltered');
      }
      
      return dispatch('searchMaterials');
    },
    
    // Очистка фильтров
    async clearAllFilters({ commit, dispatch }) {
      commit('clearFilters');
      commit('setPagination', { page: 1 });
      
      // При очистке всех фильтров загружаем базовую календарную статистику
      dispatch('loadCalendarStats');
      
      return dispatch('searchMaterials');
    },
    
    // Создание нового тега
    async createTag({ commit, dispatch }, { tag_name, description, color }) {
      commit('setError', null);
      
      try {
        const params = {
          action: 'create_material_tag',
          tag_name,
          description,
          color
        };
        
        const response = await axiosConfig.post(API_URL, params);
        
        if (response.data.result === 'success') {
          // Перезагружаем опции фильтров
          await dispatch('loadFilterOptions');
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Failed to create tag');
        }
      } catch (error) {
        console.error('Error creating tag:', error);
        commit('setError', error.message);
        throw error;
      }
    },
    
    // Создание новой локации
    async createLocation({ commit, dispatch }, { country, city, notes }) {
      commit('setError', null);
      
      try {
        const params = {
          action: 'create_material_location',
          country,
          city: city || null,
          notes: notes || null
        };
        
        const response = await axiosConfig.post(API_URL, params);
        
        if (response.data.result === 'success') {
          // Перезагружаем опции фильтров
          await dispatch('loadFilterOptions');
          return response.data.data;
        } else {
          throw new Error(response.data.message || 'Failed to create location');
        }
      } catch (error) {
        console.error('Error creating location:', error);
        commit('setError', error.message);
        throw error;
      }
    }
  },
  
  getters: {
    // Результаты поиска
    materials: state => state.materials,
    totalMaterials: state => state.totalMaterials,
    pagination: state => state.pagination,
    
    // Опции фильтров
    filterOptions: state => state.filterOptions,
    locations: state => state.filterOptions.locations,
    tags: state => state.filterOptions.tags,
    audienceLevels: state => state.filterOptions.audience_levels,
    moderationStatuses: state => state.filterOptions.moderation_statuses,
    festivals: state => state.filterOptions.festivals,
    
    // Календарная статистика
    calendarStats: state => state.calendarStats,
    
    // Фильтры
    activeFilters: state => state.activeFilters,
    hasActiveFilters: state => {
      const filters = state.activeFilters;
      return filters.query !== '' || filters.year !== null || filters.month !== null ||
             filters.country !== '' || filters.city !== '' || filters.audience_level !== '' ||
             filters.moderation_status !== '' || filters.festival_id !== null ||
             (filters.tags && filters.tags.length > 0);
    },
    
    // Состояния загрузки
    isLoading: state => state.isLoading,
    isLoadingOptions: state => state.isLoadingOptions,
    isLoadingCalendar: state => state.isLoadingCalendar,
    error: state => state.error,
    
    // Дополнительные данные
    filtersDescription: state => state.filtersDescription,
    appliedFilters: state => state.appliedFilters
  }
};

export default materialFilterModule;